import { useEffect, useState } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from "recharts";
import { Loader2, FileDown } from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { api, fmtXof } from "../../lib/api";

const PERIODS = [
  { key: "week", label: "Semaine" }, { key: "month", label: "Mois" },
  { key: "quarter", label: "Trimestre" }, { key: "semester", label: "Semestre" }, { key: "year", label: "Année" },
];

const fmtVal = (v, unit) => {
  if (v === null || v === undefined) return "—";
  if (unit === "FCFA") return fmtXof(v);
  if (unit === "j") return `${v} j`;
  return `${v}`;
};

export const TrendAnalytics = ({ base, title = "Analyse & tendances", testid = "trend-analytics" }) => {
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);
  const [showTrend, setShowTrend] = useState(true);
  const [showForecast, setShowForecast] = useState(false);
  const [showPrev, setShowPrev] = useState(false);

  useEffect(() => {
    api.get(`${base}/timeseries?period=${period}`).then(({ data }) => {
      setData(data);
      setActive((prev) => {
        if (prev) return prev;
        const init = {};
        Object.keys(data.stats).forEach((k, i) => (init[k] = i < 2));
        return init;
      });
    }).catch(() => {});
  }, [base, period]);

  const exportTs = async (format) => {
    try {
      const res = await api.get(`${base}/timeseries/export?period=${period}&format=${format}`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `${base.includes("hr") ? "analyse-rh" : "analyse-finance"}-${period}.${format === "pdf" ? "pdf" : "xlsx"}`;
      a.click(); toast.success("Export généré");
    } catch { toast.error("Export impossible"); }
  };

  const order = data ? Object.keys(data.stats) : [];
  const activeKeys = order.filter((k) => active?.[k]);
  const rightAxis = (k) => data.stats[k].unit !== "FCFA";

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid={testid}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-display text-base font-semibold text-ciel">{title}</h3>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-full bg-slate-100 p-1">
            {PERIODS.map((p) => (
              <button key={p.key} data-testid={`${testid}-period-${p.key}`} onClick={() => setPeriod(p.key)}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${period === p.key ? "bg-white text-navy shadow-sm" : "text-slate-500 hover:text-navy"}`}>{p.label}</button>
            ))}
          </div>
          <Button data-testid={`${testid}-export-xlsx`} size="sm" variant="outline" onClick={() => exportTs("xlsx")} className="btn-emboss rounded-full"><FileDown size={13} className="mr-1" /> Excel</Button>
          <Button data-testid={`${testid}-export-pdf`} size="sm" onClick={() => exportTs("pdf")} className="btn-emboss rounded-full bg-navy text-white hover:bg-navy-light"><FileDown size={13} className="mr-1" /> PDF</Button>
        </div>
      </div>

      {!data ? (
        <div className="flex h-64 items-center justify-center"><Loader2 className="animate-spin text-ciel" size={22} /></div>
      ) : (
        <>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {order.map((k) => {
              const on = active?.[k];
              const meta = data.stats[k];
              return (
                <button key={k} data-testid={`${testid}-series-${k}`} onClick={() => setActive((v) => ({ ...v, [k]: !v[k] }))}
                  className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-all ${on ? "border-transparent text-white" : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"}`}
                  style={on ? { backgroundColor: meta.color } : {}}>
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: on ? "#fff" : meta.color }} />{meta.label}
                </button>
              );
            })}
            <div className="ml-auto flex flex-wrap items-center gap-3">
              <label className="flex cursor-pointer items-center gap-1.5 text-xs font-medium text-slate-600" data-testid={`${testid}-toggle-trend`}>
                <input type="checkbox" checked={showTrend} onChange={(e) => setShowTrend(e.target.checked)} className="accent-ciel" /> Tendance
              </label>
              <label className="flex cursor-pointer items-center gap-1.5 text-xs font-medium text-slate-600" data-testid={`${testid}-toggle-forecast`}>
                <input type="checkbox" checked={showForecast} onChange={(e) => setShowForecast(e.target.checked)} className="accent-ciel" /> Prévisions
              </label>
              <label className="flex cursor-pointer items-center gap-1.5 text-xs font-medium text-slate-600" data-testid={`${testid}-toggle-prev`}>
                <input type="checkbox" checked={showPrev} onChange={(e) => setShowPrev(e.target.checked)} className="accent-ciel" /> Comparer N-1
              </label>
            </div>
          </div>

          <div className="mt-4 h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.points} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip formatter={(v, n) => [typeof v === "number" && Math.abs(v) >= 1000 ? fmtXof(v) : v, n]} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {data.forecast_count > 0 && showForecast && data.labels.length > 0 && (
                  <ReferenceLine x={data.labels[data.labels.length - 1]} stroke="#94a3b8" strokeDasharray="2 2" label={{ value: "prévision →", fontSize: 9, fill: "#94a3b8", position: "insideTopRight" }} />
                )}
                {activeKeys.map((k) => (
                  <Line key={k} {...(rightAxis(k) ? { yAxisId: "right" } : {})} type="monotone" dataKey={k} name={data.stats[k].label} stroke={data.stats[k].color} strokeWidth={2} dot={false} connectNulls />
                ))}
                {data.objectif_key && active?.[data.objectif_key] && data.objectif_value > 0 && (
                  <Line type="monotone" dataKey="objectif" name="Objectif CA" stroke="#0f172a" strokeWidth={1.5} strokeDasharray="4 4" dot={false} connectNulls />
                )}
                {showPrev && activeKeys.map((k) => (
                  <Line key={k + "_prev"} {...(rightAxis(k) ? { yAxisId: "right" } : {})} type="monotone" dataKey={k + "_prev"} name={`${data.stats[k].label} (N-1)`} stroke={data.stats[k].color} strokeWidth={2} strokeOpacity={0.3} dot={false} connectNulls legendType="none" />
                ))}
                {showTrend && activeKeys.map((k) => (
                  <Line key={k + "_trend"} {...(rightAxis(k) ? { yAxisId: "right" } : {})} type="linear" dataKey={k + "_trend"} name={`Tendance ${data.stats[k].label}`} stroke={data.stats[k].color} strokeWidth={1.5} strokeDasharray="6 4" dot={false} legendType="none" />
                ))}
                {showForecast && activeKeys.map((k) => (
                  <Line key={k + "_fc"} {...(rightAxis(k) ? { yAxisId: "right" } : {})} type="monotone" dataKey={k + "_forecast"} name={`Projection ${data.stats[k].label}`} stroke={data.stats[k].color} strokeWidth={2} strokeDasharray="2 3" dot={{ r: 2 }} connectNulls legendType="none" />
                ))}
                {showForecast && activeKeys.map((k) => [
                  <Line key={k + "_up"} {...(rightAxis(k) ? { yAxisId: "right" } : {})} type="monotone" dataKey={k + "_upper"} stroke={data.stats[k].color} strokeWidth={1} strokeOpacity={0.4} strokeDasharray="1 3" dot={false} connectNulls legendType="none" />,
                  <Line key={k + "_lo"} {...(rightAxis(k) ? { yAxisId: "right" } : {})} type="monotone" dataKey={k + "_lower"} stroke={data.stats[k].color} strokeWidth={1} strokeOpacity={0.4} strokeDasharray="1 3" dot={false} connectNulls legendType="none" />,
                ])}
              </LineChart>
            </ResponsiveContainer>
          </div>

          {data.objectif_key && data.objectif_value > 0 && (() => {
            const actual = data.points.filter((p) => !p.forecast);
            const cur = actual.length ? (actual[actual.length - 1][data.objectif_key] || 0) : 0;
            const pct = data.objectif_value > 0 ? Math.round((cur / data.objectif_value) * 100) : 0;
            const barCls = pct >= 100 ? "bg-emerald-500" : pct >= 70 ? "bg-amber-500" : "bg-rose-500";
            const txtCls = pct >= 100 ? "text-emerald-600" : pct >= 70 ? "text-amber-600" : "text-rose-600";
            return (
              <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid={`${testid}-objectif-gap`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-xs font-semibold text-navy">Atteinte de l'objectif CA — {data.labels[data.labels.length - 1]} (période en cours)</span>
                  <span className={`font-display text-lg font-bold ${txtCls}`} data-testid={`${testid}-objectif-pct`}>{pct} %</span>
                </div>
                <div className="mt-2 h-2.5 w-full overflow-hidden rounded-full bg-slate-200">
                  <div className={`h-full rounded-full transition-all ${barCls}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                </div>
                <p className="mt-1.5 text-[11px] text-slate-500">Réalisé <b>{fmtXof(cur)}</b> sur un objectif de <b>{fmtXof(data.objectif_value)}</b>{pct < 100 ? ` · reste ${fmtXof(Math.max(data.objectif_value - cur, 0))}` : " · objectif atteint 🎉"}</p>
              </div>
            );
          })()}

          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {activeKeys.map((k) => {
              const st = data.stats[k];
              return (
                <div key={k} className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid={`${testid}-stat-${k}`}>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1.5 text-xs font-semibold text-navy"><span className="h-2 w-2 rounded-full" style={{ backgroundColor: st.color }} />{st.label}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${st.significant ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>{st.significant ? "Significatif" : "Non signif."}</span>
                  </div>
                  <p className="mt-1.5 font-mono text-xs text-slate-700">{st.equation}</p>
                  <p className="mt-1 text-[11px] text-slate-500">R² = <b>{st.r2}</b> · p = <b>{st.p_value}</b> · {st.direction === "hausse" ? "↗ hausse" : st.direction === "baisse" ? "↘ baisse" : "→ stable"}</p>
                  <p className="mt-0.5 text-[10px] italic text-slate-400">{st.interpretation}</p>
                  {showForecast && st.forecast && (
                    <div className="mt-2 border-t border-slate-200 pt-2">
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Projection (IC 95 %)</p>
                      {st.forecast.map((f, i) => (
                        <p key={i} className="text-[11px] text-slate-600">P+{i + 1} : <b>{fmtVal(f.value, st.unit)}</b>{f.lower !== null && <span className="text-slate-400"> [{fmtVal(f.lower, st.unit)} – {fmtVal(f.upper, st.unit)}]</span>}</p>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {activeKeys.length === 0 && <p className="text-xs text-slate-400">Sélectionnez au moins une série pour afficher la tendance.</p>}
          </div>
        </>
      )}
    </div>
  );
};
